import { z } from "zod";
/** Статус результата сверки */
export const VerificationStatusEnum = z.enum([
    "match",
    "mismatch",
    "missing_in_act",
    "missing_in_order",
]);
/** Запрос на запуск сверки */
export const RunVerificationRequestSchema = z.object({
    objectName: z.string().min(1),
    periodMonth: z.number().int().min(1).max(12),
    periodYear: z.number().int().min(2000),
});
/** Сводка сверки */
export const VerificationSummarySchema = z.object({
    totalLines: z.number().int(),
    matchedLines: z.number().int(),
    mismatchedLines: z.number().int(),
    missingInAct: z.number().int(),
    missingInOrder: z.number().int(),
    workOrderTotal: z.number().int(),
    completionActTotal: z.number().int(),
    difference: z.number().int(),
});
/** Ответ на запуск сверки */
export const RunVerificationResponseSchema = z.object({
    verificationId: z.string().uuid(),
    objectName: z.string(),
    periodMonth: z.number(),
    periodYear: z.number(),
    workOrderFiles: z.array(z.number()),
    completionActFiles: z.array(z.number()),
    rateBookFile: z.number().nullable(),
    status: z.string(),
    summary: VerificationSummarySchema,
});
/** Данные строки в результате сверки */
const VerificationLineDataSchema = z.object({
    lineId: z.number(),
    workName: z.string(),
    unit: z.string().nullable(),
    quantity: z.number().int(),
    rate: z.number().int(),
    amount: z.number().int(),
});
/** Расхождения */
const DiscrepanciesSchema = z.object({
    quantityMatch: z.boolean(),
    rateMatch: z.boolean(),
    amountMatch: z.boolean(),
    nameSimilarity: z.number(),
});
/** Результат сверки одной строки */
export const VerificationResultSchema = z.object({
    id: z.number(),
    rateNumber: z.string(),
    status: VerificationStatusEnum,
    workOrder: VerificationLineDataSchema.nullable(),
    completionAct: VerificationLineDataSchema.nullable(),
    discrepancies: DiscrepanciesSchema.nullable(),
});
/** Детальные результаты сверки */
export const VerificationDetailResponseSchema = z.object({
    verificationId: z.string(),
    objectName: z.string(),
    periodMonth: z.number(),
    periodYear: z.number(),
    results: z.array(VerificationResultSchema),
    summary: VerificationSummarySchema,
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
});
/** Запись истории сверок */
export const VerificationHistoryItemSchema = z.object({
    verificationId: z.string(),
    objectName: z.string(),
    periodMonth: z.number(),
    periodYear: z.number(),
    totalLines: z.number(),
    mismatchedLines: z.number(),
    createdAt: z.string(),
});
/** Ответ истории сверок */
export const VerificationHistoryResponseSchema = z.object({
    verifications: z.array(VerificationHistoryItemSchema),
    total: z.number(),
});
/** Параметры запроса результатов сверки */
export const VerificationResultsQuerySchema = z.object({
    status: VerificationStatusEnum.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
// ─── Проверка наряда (008-work-order-check) ───
/** Запрос проверки наряда */
export const CheckWorkOrderRequestSchema = z.object({
    fileId: z.number().int().positive(),
});
// ─── Контракты сверки (019-check-engine-v2): Source Refs / Trace ───
/** Указатель на конкретную строку наряда (для трассировки нарушений сверки). */
export const WorkOrderSourceRefSchema = z.object({
    fileId: z.number(),
    fileName: z.string(),
    /** Имя листа в исходном Excel. Используется preview-страницей для активации правильного листа (фича 028). */
    sheetName: z.string().nullable().optional(),
    /** Порядковый счётчик строки в наряде (1..N среди валидных позиций). Не совпадает с реальной Excel-строкой. */
    rowNumber: z.number(),
    /** Реальная 1-based Excel-строка (фича 029). null для строк, добавленных до 029
     *  и пока не пере-парсенных. UI fallback при формировании ссылок: excelRow ?? rowNumber. */
    excelRow: z.number().nullable().optional(),
    rateNumber: z.string().nullable(),
    workName: z.string(),
    unit: z.string().nullable(),
    quantity: z.number(),
    /** Базовая расценка (графа 7), без k. */
    rate: z.number(),
    /** k-коэффициент (графа 8). null/0 в правилах трактуется как 1. */
    k: z.number().nullable(),
    /** = quantity × rate × k. */
    amount: z.number(),
    /** true — для подозрительных позиций внутри агрегатных нарушений. */
    isSuspect: z.boolean().optional(),
});
/** Указатель на конкретную позицию акта (для трассировки нарушений сверки). */
export const ActSourceRefSchema = z.object({
    fileId: z.number(),
    fileName: z.string(),
    /** Имя листа в исходном Excel. Используется preview-страницей для активации правильного листа (фича 028). */
    sheetName: z.string().nullable().optional(),
    blockId: z.number(),
    blockNumber: z.number(),
    blockHeader: z.string().nullable(),
    /** ID строки работы (заполнено для построчных нарушений; null для блок-уровневых). */
    lineId: z.number().optional(),
    /** Реальная 1-based Excel-строка. Sprint 6 / C11.1: выровнено с WO —
     *  `nullable().optional()` вместо просто optional. null для строк до фичи 029. */
    excelRow: z.number().nullable().optional(),
    /** Excel-строки многострочного наименования. */
    continuationRows: z.array(z.number()).optional(),
    code: z.string().nullable(),
    workName: z.string(),
    unit: z.string().nullable(),
    quantity: z.number(),
    /** Уже скорректированная ставка (с применённым k). */
    salaryRate: z.number(),
    salaryTotal: z.number(),
    /**
     * Σ доп. работ (extra labor): salaryTotal+extraLaborTotal даёт полный вклад
     * строки в гранд-тотал актов (RC-G1, FR-013). Optional — для обратной
     * совместимости со старыми снэпшотами; при отсутствии трактовать как 0.
     */
    extraLaborTotal: z.number().optional(),
    /** true — для подозрительных позиций внутри агрегатных нарушений. */
    isSuspect: z.boolean().optional(),
});
/** Трассировка источников нарушения (для правил сверки set-rules). */
export const ViolationSourceTraceSchema = z.object({
    workOrderSources: z.array(WorkOrderSourceRefSchema),
    actSources: z.array(ActSourceRefSchema),
    comparison: z.object({
        /** Сравниваемое поле (quantity / rate / cost / другое). */
        field: z.string(),
        /** Эталон со стороны нарядов. null — если не применимо. */
        expected: z.union([z.number(), z.string(), z.null()]),
        /** Факт со стороны актов. null — если не применимо. */
        actual: z.union([z.number(), z.string(), z.null()]),
        /** Разница (actual - expected для чисел; описание для строк). null — если не применимо. */
        diff: z.union([z.number(), z.string(), z.null()]),
        /** Процентная разница (если применимо). */
        diffPercent: z.number().optional(),
        /** Единица измерения для отображения. */
        unit: z.string().optional(),
    }),
});
/** Нарушение одного правила */
export const RuleViolationSchema = z.object({
    ruleId: z.string(),
    ruleLabel: z.string(),
    ruleName: z.string(),
    severity: z.enum(["error", "warning"]),
    field: z.string(),
    expected: z.string().nullable(),
    actual: z.string().nullable(),
    message: z.string(),
    metadata: z.record(z.any()).optional(), // Дополнительные данные (similarity, canonicalUnit)
    canAddSynonym: z.boolean().optional(), // Можно ли добавить синоним (только для unit-match rule)
    /** Полная трассировка источников. Заполнена для правил сверки (set-rules) — feature 019. */
    trace: ViolationSourceTraceSchema.optional(),
    /** Ключ группировки в Discrepancy. Заполнен для set-rules. */
    groupKey: z.string().optional(),
    /** Идентификатор исключения, погасившего нарушение (этап 6 v2). В первой версии всегда null/undefined. */
    mutedBy: z.string().nullable().optional(),
    /** Ссылка на правило исключения (этап 6 v2). В первой версии всегда null/undefined. */
    exceptionId: z.number().nullable().optional(),
});
/** Результат проверки одной строки */
export const LineCheckResultSchema = z.object({
    rowNumber: z.number().int(),
    rateNumber: z.string().nullable(),
    workName: z.string(),
    unit: z.string().nullable(),
    quantity: z.number(),
    rate: z.number().int(),
    k: z.number().nullable(),
    amount: z.number().int(),
    passed: z.boolean(),
    violations: z.array(RuleViolationSchema),
});
/** Ответ проверки наряда */
export const CheckWorkOrderResponseSchema = z.object({
    fileId: z.number(),
    fileName: z.string(),
    totalLines: z.number().int(),
    errorCount: z.number().int(),
    warningCount: z.number().int(),
    passedCount: z.number().int(),
    lines: z.array(LineCheckResultSchema),
});
// ─── Discrepancy (019-check-engine-v2): top-level контейнер расхождения сверки ───
/**
 * Discrepancy — основной UI/DB-примитив сверки.
 * Группирует все RuleViolation, происходящие из одной первопричины
 * (одна расценка, один вид работ, или гранд-тотал).
 */
export const DiscrepancySchema = z.object({
    /** Уникальный ID в рамках одного прогона сверки. */
    id: z.string(),
    /** Ключ группировки: код расценки / название вида работ / "grand-total". */
    groupKey: z.string(),
    /** Уровень детализации. Sprint 3 / A4: `aggregate-by-work-kind` удалён. */
    level: z.enum(["position", "grand-total", "unified"]),
    /** Максимальное severity среди вошедших violations. */
    severity: z.enum(["error", "warning"]),
    /** Режимы, в которых обнаружено расхождение. */
    modes: z.array(z.enum(["volume", "rate", "cost"])),
    /** Сводное сообщение в стиле «полный с источниками». */
    summary: z.string(),
    /** Все элементарные нарушения, входящие в это расхождение. */
    violations: z.array(RuleViolationSchema),
    /** Объединённые источники со стороны нарядов (по уникальности fileId+rowNumber). */
    workOrderSources: z.array(WorkOrderSourceRefSchema),
    /** Объединённые источники со стороны актов (по уникальности fileId+lineId). */
    actSources: z.array(ActSourceRefSchema),
    /** ID связанных Discrepancy на других уровнях детализации. */
    relatedDiscrepancyIds: z.array(z.string()),
    /** Произвольные доп. данные для UI (например, similarity scores). */
    metadata: z.record(z.unknown()).optional(),
});
// ─── Словарь синонимов единиц измерения ───
/** Синоним единицы измерения */
export const UnitSynonymSchema = z.object({
    id: z.number().int().positive(),
    canonicalUnit: z.string().min(1),
    synonym: z.string().min(1),
    createdAt: z.string(), // ISO 8601
    createdBy: z.string().nullable().optional(),
});
/** Запрос добавления синонима */
export const AddUnitSynonymRequestSchema = z.object({
    canonicalUnit: z.string().min(1),
    synonym: z
        .string()
        .min(1)
        .refine((s) => s.trim().length > 0, {
        message: "Синоним не может быть пустым",
    }),
});
/** Запрос редактирования синонима */
export const UpdateUnitSynonymRequestSchema = z.object({
    synonym: z
        .string()
        .min(1)
        .refine((s) => s.trim().length > 0, {
        message: "Синоним не может быть пустым",
    }),
});
/** Синоним с ID для UI управления */
export const UnitSynonymItemSchema = z.object({
    id: z.number().int().positive(),
    synonym: z.string(),
});
/** Группировка синонимов по канонической единице */
export const UnitSynonymGroupSchema = z.object({
    canonicalUnit: z.string(),
    synonyms: z.array(UnitSynonymItemSchema),
});
/** Ответ получения всех синонимов */
export const GetUnitSynonymsResponseSchema = z.object({
    synonyms: z.array(UnitSynonymGroupSchema),
});
//# sourceMappingURL=verification.js.map